import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function useUserPreferences<T = unknown>(key: string, defaultValue: T) {
  const { user } = useAuth();
  const [value, setValueState] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch on mount
  useEffect(() => {
    if (!user) {
      return;
    }

    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('user_id', user.id)
        .eq('key', key)
        .maybeSingle();

      if (cancelled) return;

      if (!error && data?.value != null) {
        setValueState(data.value as T);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, key]);

  // Persist to Supabase (debounced — waits 2s after last call)
  const persistToSupabase = useCallback(
    (newValue: T) => {
      if (!user) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(async () => {
        await supabase.from('user_preferences').upsert(
          {
            user_id: user.id,
            key,
            value: newValue as unknown as Record<string, unknown>,
          },
          { onConflict: 'user_id,key' },
        );
      }, 2000);
    },
    [user, key],
  );

  // Set value locally + schedule Supabase write
  const setValue = useCallback(
    (newValue: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved =
          typeof newValue === 'function' ? (newValue as (p: T) => T)(prev) : newValue;
        persistToSupabase(resolved);
        return resolved;
      });
    },
    [persistToSupabase],
  );

  // Force immediate save (for beforeunload / visibilitychange)
  const saveNow = useCallback(async () => {
    if (!user) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    await supabase.from('user_preferences').upsert(
      {
        user_id: user.id,
        key,
        value: value as unknown as Record<string, unknown>,
      },
      { onConflict: 'user_id,key' },
    );
  }, [user, key, value]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return { value, setValue, loading, saveNow };
}
